/**
 * Roles Management React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RoleService } from '../api/services/roleService';
import type { CreateRoleRequest, UpdateRoleRequest } from '../api/types';

// Query keys
export const roleKeys = {
  all: ['roles'] as const,
  list: (params?: any) => [...roleKeys.all, 'list', params] as const,
  detail: (id: string) => [...roleKeys.all, id] as const,
  permissions: {
    all: ['permissions'] as const,
    list: () => [...roleKeys.permissions.all, 'list'] as const,
    grouped: () => [...roleKeys.permissions.all, 'grouped'] as const,
  },
};

// Roles
export function useRoles(params?: {
  page?: number;
  size?: number;
  scope?: string;
  organization_id?: string;
  search?: string;
  include_system?: boolean;
}) {
  return useQuery({
    queryKey: roleKeys.list(params),
    queryFn: () => RoleService.listRoles(params),
  });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: roleKeys.detail(id),
    queryFn: async () => {
      const data = await RoleService.getRole(id);
      return { success: true, data };
    },
    enabled: !!id,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => RoleService.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      RoleService.updateRole(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RoleService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

export function useCloneRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; organization_id?: string } }) =>
      RoleService.cloneRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

// Role permissions
export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: Array<{ permission_id: string; granted?: boolean; conditions?: any }> }) =>
      RoleService.updateRolePermissions(roleId, permissions),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
    },
  });
}

export function useAddRolePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permission_id, granted = true, conditions = {} }: { roleId: string; permission_id: string; granted?: boolean; conditions?: any }) =>
      RoleService.addRolePermission(roleId, { permission_id, granted, conditions }),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
    },
  });
}

export function useRemoveRolePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissionId }: { roleId: string; permissionId: string }) =>
      RoleService.removeRolePermission(roleId, permissionId),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
    },
  });
}

// Permissions
export function usePermissions(params?: {
  category?: string;
  resource?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: roleKeys.permissions.list(),
    queryFn: () => RoleService.listPermissions(params),
  });
}

export function usePermissionsGrouped() {
  return useQuery({
    queryKey: roleKeys.permissions.grouped(),
    queryFn: async () => {
      const data = await RoleService.getPermissionsGrouped();
      return { success: true, data };
    },
    staleTime: 300000, // 5 minutes - permissions rarely change
  });
}
